import { useParams, Link } from 'react-router-dom';
import useReveal from '../components/useReveal';
import { works } from '../data/works';
import { usePost } from '../hooks/usePosts';

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useReveal();
  return <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

export default function BlogPost() {
  const { id } = useParams();
  const { post, loading, allPosts } = usePost(id);

  if (loading) {
    return <div className="pt-32 px-20 text-stone-muted">Loading...</div>;
  }

  if (!post) {
    return <div className="pt-32 px-20 text-stone-muted">Post not found.</div>;
  }

  const linkedWork = works.find(w => w.id === post.workId);
  const otherPosts = allPosts.filter(p => p.id !== id && p.slug !== id).slice(0, 2);

  return (
    <div key={id} className="pt-16">

      {/* Back */}
      <div className="px-10 md:px-16 lg:px-20 py-6 border-b border-cream-400/60">
        <Link to="/blog" className="label text-stone-muted hover:text-ink transition-colors">
          ← All blog posts
        </Link>
      </div>

      {/* Post header */}
      <div className="px-10 md:px-16 lg:px-20 py-20 border-b border-cream-400/60 max-w-3xl">
        <Reveal key={id}>
          <div className="flex gap-2 flex-wrap mb-8">
            {post.tags.map(t => (
              <span key={t} className="label px-2 py-0.5 border border-cream-400/60 bg-[#F8F3D6] text-ink">{t}</span>
            ))}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink font-normal leading-tight mb-3">
            {post.title}
          </h1>
          <p className="font-serif text-xl text-stone-warm italic mb-8">{post.subtitle}</p>
          <div className="flex gap-6 items-center">
            <span className="font-mono text-[11px] text-stone-muted">{post.date}</span>
            <span className="font-mono text-[11px] text-stone-muted">{post.readTime} read</span>
            {linkedWork && (
              <Link to={`/works/${linkedWork.id}`}
                className="label text-stone-muted hover:text-ink transition-colors border-b border-cream-400/60 pb-px">
                {linkedWork.title} →
              </Link>
            )}
          </div>
        </Reveal>
      </div>

      {/* Body — two column at wide, single below */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] border-b border-cream-400/60">

        {/* Article body */}
        <div className="px-10 md:px-16 lg:px-20 py-16 border-b lg:border-b-0 lg:border-r border-cream-400/60">
          <div className="max-w-xl">
            <div className="prose prose-stone max-w-none font-sans text-base text-stone-warm leading-[1.85]" dangerouslySetInnerHTML={{ __html: post.body }} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="px-10 py-16 flex flex-col gap-10">
          {/*  */}

          <Reveal key={id} delay={100}>
            <p className="label mb-4">More posts</p>
            <div className="space-y-4">
              {otherPosts.map(p => (
                <Link key={p.id} to={`/blog/${p.id}`}
                  className="group block border-b border-cream-400/60 pb-4 last:border-b-0">
                  <p className="font-serif text-sm text-ink mb-1 group-hover:opacity-70 transition-opacity leading-snug">
                    {p.title}
                  </p>
                  <p className="font-mono text-[10px] text-stone-muted">{p.date} · {p.readTime} read</p>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

    </div>
  );
}
